import { useMemo, useState } from 'react';
import { Box, ChevronDown, ChevronRight, GitBranch } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

function ValidationBadge({ validation }) {
  if (!validation) return null;
  const ok = validation.valid !== false;
  return (
    <span
      className={`text-[9px] px-1.5 py-0.5 rounded border ${
        ok
          ? 'border-emerald-500/30 text-emerald-300/90'
          : 'border-red-500/30 text-red-300/90'
      }`}
    >
      {ok ? 'graph valid' : `${validation.issues?.length || 0} issue(s)`}
    </span>
  );
}

function EdmCard({ entry, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const edm = entry.edm || {};
  const nodes = edm.nodes || [];
  const edges = edm.edges || [];
  const rf = entry.react_flow || {};

  return (
    <div className="rounded-lg border border-violet-500/20 bg-violet-950/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-violet-950/20"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 text-violet-400 shrink-0 mt-0.5" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-violet-100">
            <span className="font-mono text-[11px] text-violet-400/90">{entry.artifact_id}</span>
            {' · '}
            {edm.artifact_type || 'EDM'}
          </p>
          <p className="text-xs text-slate-400 line-clamp-1">{edm.name}</p>
          <p className="text-[10px] text-slate-600 mt-0.5">
            {entry.slide_id} · {nodes.length} nodes · {edges.length} edges
          </p>
        </div>
        <ValidationBadge validation={entry.validation} />
      </button>
      {open ? (
        <div className="px-3 pb-3 space-y-3 border-t border-violet-900/40 pt-3 text-xs">
          {edm.description ? (
            <p className="text-slate-400">{edm.description}</p>
          ) : null}
          {nodes.length ? (
            <div>
              <p className="text-slate-500 mb-1">Nodes</p>
              <ul className="space-y-0.5 font-mono text-[10px] text-slate-300">
                {nodes.map((n) => (
                  <li key={n.id}>
                    {n.id}
                    {' '}
                    <span className="text-slate-500">
                      ({n.type}) {n.label}
                      {' @ '}
                      ({n.position?.x ?? 0},{n.position?.y ?? 0})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {edges.length ? (
            <div>
              <p className="text-slate-500 mb-1">Edges</p>
              <ul className="space-y-0.5 font-mono text-[10px] text-slate-300">
                {edges.map((e) => (
                  <li key={e.id}>
                    {e.source} → {e.target}
                    {e.type ? <span className="text-slate-500"> ({e.type})</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {edm.layout ? (
            <p className="text-slate-500">
              Layout: <span className="text-slate-300">{edm.layout.engine}</span>
              {' · '}
              {edm.layout.layout_type}
              {' · '}
              {edm.layout.direction}
              {edm.layout.auto_layout_enabled ? ' · auto' : ''}
            </p>
          ) : null}
          {rf.nodes?.length ? (
            <p className="text-slate-500">
              React Flow export: {rf.nodes.length} nodes, {rf.edges?.length || 0} edges
            </p>
          ) : null}
          {entry.version?.version_number ? (
            <p className="text-[10px] text-slate-600">
              Version {entry.version.version_number}
              {entry.version.change_summary ? ` — ${entry.version.change_summary}` : ''}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function ArtifactEdmV3Panel({ data }) {
  const edms = data?.editable_diagram_models || [];
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const types = useMemo(() => {
    const set = new Set(edms.map((e) => e.edm?.artifact_type).filter(Boolean));
    return Array.from(set).sort();
  }, [edms]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return edms.filter((e) => {
      const t = e.edm?.artifact_type;
      if (typeFilter && t !== typeFilter) return false;
      if (!q) return true;
      return (
        e.artifact_id?.toLowerCase().includes(q)
        || e.edm?.name?.toLowerCase().includes(q)
        || e.slide_id?.toLowerCase().includes(q)
      );
    });
  }, [edms, filter, typeFilter]);

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-violet-500/25 bg-violet-950/15 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-violet-200">
          <GitBranch className="w-4 h-4" />
          {data.total_edms} Editable Diagram Models
          {data.step === '5C.1' ? (
            <span className="text-[10px] text-violet-400/70">(Step 5C.1)</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Box className="w-4 h-4" />
          Canonical source of truth — no SVG, renderer-independent
        </div>
        {data.source ? (
          <span className="text-[10px] text-slate-500 ml-auto">Source: {data.source}</span>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {types.length > 1 ? (
          <Select value={typeFilter || '__all__'} onValueChange={(v) => setTypeFilter(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-8 w-[200px] text-xs border-slate-700 bg-slate-900 text-slate-300">
              <SelectValue placeholder="All artifact types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All artifact types</SelectItem>
              {types.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        {edms.length > 4 ? (
          <input
            type="search"
            placeholder="Filter EDMs…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 min-w-[140px] text-sm rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-slate-200"
          />
        ) : null}
      </div>

      <div className="space-y-2 max-h-[65vh] overflow-y-auto">
        {filtered.map((entry, i) => (
          <EdmCard key={entry.artifact_id} entry={entry} defaultOpen={i < 2} />
        ))}
      </div>
    </div>
  );
}
